/**
 * Regras do Regulamento FRL 2026 v1.2 por modalidade — espelho de
 * backend/src/modules/tournaments/services/modalityDefaults.ts. São o padrão
 * dos campos de criação/edição; o organizador pode alterar tudo.
 */
export interface IModalityRules {
  time_limit_seconds: number;
  max_rounds: number;
  bonus_time_seconds: number;
  bonus_points: number;
}

export interface IModalityRegulation extends IModalityRules {
  evaluation_type: 'binary' | 'subjective';
  penalty_per_occurrence: number;
  /** Como o regulamento descreve a regra, para mostrar ao lado do campo. */
  summary: string;
}

export const MODALITY_REGULATION: Record<string, IModalityRegulation> = {
  robo_pintor: {
    time_limit_seconds: 300,
    max_rounds: 3,
    bonus_time_seconds: 180,
    bonus_points: 20,
    evaluation_type: 'subjective',
    penalty_per_occurrence: 10,
    summary:
      'Regulamento: rodada de até 5 min · bônus de 20 pts em menos de 3 min',
  },
  seguidor_de_linha: {
    time_limit_seconds: 180,
    max_rounds: 3,
    bonus_time_seconds: 120,
    bonus_points: 20,
    evaluation_type: 'binary',
    penalty_per_occurrence: 5,
    summary:
      'Regulamento: rodada de até 3 min · bônus de 20 pts em menos de 2 min',
  },
};

export const MAX_BONUS_POINTS = 100;

export function regulationFor(slug: string): IModalityRegulation {
  return MODALITY_REGULATION[slug] ?? MODALITY_REGULATION.seguidor_de_linha;
}

export function regulationRules(slug: string): IModalityRules {
  const { time_limit_seconds, max_rounds, bonus_time_seconds, bonus_points } =
    regulationFor(slug);
  return { time_limit_seconds, max_rounds, bonus_time_seconds, bonus_points };
}

/** Campos editáveis podem estar vazios enquanto a pessoa digita. */
export type TModalityRulesDraft = {
  [K in keyof IModalityRules]: number | null;
};

export type TModalityRulesErrors = Partial<
  Record<keyof IModalityRules, string>
>;

const isPositiveInt = (value: number | null): value is number =>
  value !== null && Number.isInteger(value) && value > 0;

/**
 * Mesma validação do backend (BE-32), feita enquanto a pessoa digita: o bônus
 * precisa caber na rodada e os pontos ficam entre 0 e 100.
 */
export function validateModalityRules(
  draft: TModalityRulesDraft,
  { requireRounds = true } = {},
): TModalityRulesErrors {
  const errors: TModalityRulesErrors = {};

  if (!isPositiveInt(draft.time_limit_seconds)) {
    errors.time_limit_seconds = 'Informe a duração da rodada.';
  }

  if (requireRounds && !isPositiveInt(draft.max_rounds)) {
    errors.max_rounds = 'Informe ao menos 1 rodada.';
  }

  if (!isPositiveInt(draft.bonus_time_seconds)) {
    errors.bonus_time_seconds = 'Informe o tempo do bônus.';
  } else if (
    isPositiveInt(draft.time_limit_seconds) &&
    draft.bonus_time_seconds >= draft.time_limit_seconds
  ) {
    errors.bonus_time_seconds = `Precisa ser menor que a rodada (${formatDuration(
      draft.time_limit_seconds,
    )}).`;
  }

  if (
    draft.bonus_points === null ||
    !Number.isInteger(draft.bonus_points) ||
    draft.bonus_points < 0 ||
    draft.bonus_points > MAX_BONUS_POINTS
  ) {
    errors.bonus_points = `Entre 0 e ${MAX_BONUS_POINTS} pontos.`;
  }

  return errors;
}

export const hasErrors = (errors: TModalityRulesErrors): boolean =>
  Object.keys(errors).length > 0;

/** 180 → "3 min"; 108 → "1 min 48 s"; 45 → "45 s". */
export function formatDuration(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return `${seconds} s`;
  if (seconds === 0) return `${minutes} min`;
  return `${minutes} min ${seconds} s`;
}

/** Tempo de desempate: 95 → "1:35"; null → "—". */
export function formatClock(totalSeconds: number | null | undefined): string {
  if (totalSeconds === null || totalSeconds === undefined) return '—';
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}
